import { useEffect } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { __t } from '../../i18n';
import { IconDown, IconUp } from '../../icons';
import { Text } from '../../styles';

/** How long the hint holds playback back before starting on its own. */
export const HINT_DURATION = 5000;

/** Long enough to read as a dissolve; playback is released at the start of it, so the tail
 *  overlaps the first frames of the video. */
const FADE_DURATION = 400;

type SwipeHintProps = {
  visible: boolean;
  onDismiss: () => void;
};

/** First-run coaching for the vertical swipe. Tapping starts playback early. */
export default function SwipeHint({ visible, onDismiss }: SwipeHintProps) {
  useEffect(() => {
    if (!visible) return;
    const timer = setTimeout(onDismiss, HINT_DURATION);
    return () => clearTimeout(timer);
  }, [visible, onDismiss]);

  return (
    <Overlay visible={visible} onClick={onDismiss}>
      <Arrow><IconUp /></Arrow>
      <Text variant="body1" align="center" weight="medium">{__t('shorts.hint.next')}</Text>
      <Text variant="body1" align="center" weight="medium">{__t('shorts.hint.prev')}</Text>
      <Arrow><IconDown /></Arrow>
      <Text variant="body2" align="center" color="weak">{__t('shorts.hint.dismiss')}</Text>
      <ProgressTrack>
        <ProgressFill />
      </ProgressTrack>
    </Overlay>
  );
}

const bounce = keyframes({
  '0%, 100%': { transform: 'translateY(-15%)' },
  '50%': { transform: 'translateY(15%)' },
});

const fill = keyframes({
  from: { width: '0%' },
  to: { width: '100%' },
});

const Overlay = styled.div<{ visible: boolean }>(({ theme, visible }) => ({
  position: 'absolute',
  inset: 0,
  zIndex: 5,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(4),
  borderRadius: 'inherit',
  backgroundColor: theme.rgba(theme.palette.background[900][0], 0.75),
  opacity: visible ? 1 : 0,
  // Dissolves rather than vanishing, and stops swallowing taps the moment it starts leaving.
  pointerEvents: visible ? 'auto' : 'none',
  transition: `opacity ${FADE_DURATION}ms ease`,
}));

const Arrow = styled.span(({ theme }) => ({
  display: 'flex',
  color: theme.palette.text[900][1],
  animation: `${bounce} 1.4s ease-in-out infinite`,
  svg: {
    width: theme.spacing(4),
    height: theme.spacing(4),
  },
}));

const ProgressTrack = styled.div(({ theme }) => ({
  width: '60%',
  maxWidth: '16rem',
  height: 3,
  overflow: 'hidden',
  borderRadius: theme.radius.pill,
  backgroundColor: theme.rgba(theme.palette.text[900][1], 0.25),
}));

const ProgressFill = styled.div(({ theme }) => ({
  height: '100%',
  borderRadius: 'inherit',
  backgroundColor: theme.palette.primary[900][0],
  animation: `${fill} ${HINT_DURATION}ms linear forwards`,
}));
